import { AgriculturalProductMeasurers, CashFlowExpenseType, CashFlowOtherExpenseType, LoanAction, LoanStatus, LoanType, NonFarmIncomeType, OtherFarmIncomeType, ScorecardLevel } from "../shared/Enums";
import SelectOption from "../shared/models/selectOption"
import Applicant from "models/applicantForm/applicant";
import { format } from "date-fns";
import NAICSCode from "models/operatingLoan/naicsCode";
import { getYear } from "date-fns";
import JointApplicant from "models/applicantForm/jointApplicant";

class LoanApplicationUtility {
    public GetLoanTypeText(loanType: number): string {
        switch (loanType) {
            case LoanType.PurchaseFixedAsset:
                return 'Purchase of Fixed Asset';
            case LoanType.RefinanceDebt:
                return 'Refinance of Debt';
            case LoanType.OperatingLoan:
                return 'Operating Loan';
            case LoanType.DealerPurchase:
                return 'Equipment Purchase';
            case LoanType.EquipmentRefinance:
                return 'Equipment Refinance';
            case LoanType.GapFinancing:
                return 'Wildfire Gap Financing';
            case LoanType.ProductionLineOfCredit:
                return 'Production Line of Credit';
            case LoanType.EquipmentAuctionPurchase:
                return 'Equipment Auction Purchase';
            default:
                return '';
        }
    }

    public GetLoanStatusText(loanType: number): string {
        switch (loanType) {
            case LoanStatus.Submitted:
                return 'Submitted';
            case LoanStatus.Processing:
                return 'Processing';
            case LoanStatus.NeedsInput:
                return 'More Information Needed';
            case LoanStatus.Prequalified:
                return 'Pre-Qualified';
            case LoanStatus.OfferSent:
                return 'Pre-Qualification Offer Sent';
            case LoanStatus.PrequalOfferAccepted:
                return 'Pre-Qualification Offer Accepted';
            case LoanStatus.PrequalOfferRejected:
                return 'Pre-Qualification Offer Rejected';
            case LoanStatus.Approved:
                return 'Approved By Bank';
            case LoanStatus.DocumentsSent:
                return 'Documents Sent';
            case LoanStatus.ConditionsMet:
                return 'Conditions Met';
            case LoanStatus.Withdrawn:
                return 'Withdrawn';
            case LoanStatus.PrequalDenied:
                return 'Denied (Pre-Qualification)';
            case LoanStatus.BankDenied:
                return 'Denied (Bank)';
            case LoanStatus.PrequalNotAccepted:
                return 'Pre-Qualified, Not Accepted';
            case LoanStatus.PrequalAuctionLost:
                return 'Pre-Qualified, Auction Lost';
            default:
                return '';
        }
    }

    public GetLoanScorecardLevel(level: number): string {
        switch (level) {
            case ScorecardLevel.Level1:
                return 'Level 1';
            case ScorecardLevel.Level2:
                return 'Level 2';
            case ScorecardLevel.Level3:
                return 'Level 3';
            default:
                return '';
        }
    }

    public GetLoanTypeOptions(): SelectOption[] {
        return [
            {
                value: LoanType.PurchaseFixedAsset,
                label: this.GetLoanTypeText(LoanType.PurchaseFixedAsset)
            } as SelectOption,
            {
                value: LoanType.RefinanceDebt,
                label: this.GetLoanTypeText(LoanType.RefinanceDebt)
            } as SelectOption,
            {
                value: LoanType.OperatingLoan,
                label: this.GetLoanTypeText(LoanType.OperatingLoan)
            } as SelectOption,
            {
                value: LoanType.DealerPurchase,
                label: this.GetLoanTypeText(LoanType.DealerPurchase)
            } as SelectOption,
            {
                value: LoanType.EquipmentRefinance,
                label: this.GetLoanTypeText(LoanType.EquipmentRefinance)
            } as SelectOption,
            {
                value: LoanType.GapFinancing,
                label: this.GetLoanTypeText(LoanType.GapFinancing)
            } as SelectOption,
            {
                value: LoanType.ProductionLineOfCredit,
                label: this.GetLoanTypeText(LoanType.ProductionLineOfCredit)
            } as SelectOption,
            {
                value: LoanType.EquipmentAuctionPurchase,
                label: this.GetLoanTypeText(LoanType.EquipmentAuctionPurchase)
            } as SelectOption
        ];
    }

    public GetLoanScorecardLevelOptions(): SelectOption[] {
        return [
            {
                value: ScorecardLevel.Level1,
                label: 'Level 1'
            } as SelectOption,
            {
                value: ScorecardLevel.Level2,
                label: 'Level 2'
            } as SelectOption,
            {
                value: ScorecardLevel.Level3,
                label: 'Level 3'
            } as SelectOption
        ];
    }

    public GetActiveLoanStatusOptions(): SelectOption[] {
        return [
            {
                value: LoanStatus.Submitted,
                label: 'Submitted'
            } as SelectOption,
            {
                value: LoanStatus.Processing,
                label: 'Processing'
            } as SelectOption,
            {
                value: LoanStatus.NeedsInput,
                label: 'More Information Needed'
            } as SelectOption,
            {
                value: LoanStatus.Prequalified,
                label: 'Pre-Qualified'
            } as SelectOption,
            {
                value: LoanStatus.OfferSent,
                label: 'Pre-Qualification Offer Sent'
            } as SelectOption,
            {
                value: LoanStatus.PrequalOfferAccepted,
                label: 'Pre-Qualification Offer Accepted'
            } as SelectOption,
            {
                value: LoanStatus.PrequalOfferRejected,
                label: 'Pre-Qualification Offer Rejected'
            } as SelectOption,
            {
                value: LoanStatus.Approved,
                label: 'Bank Approved'
            } as SelectOption,
            {
                value: LoanStatus.DocumentsSent,
                label: 'Documents Sent'
            } as SelectOption
        ];
    }

    public GetClosedLoanStatusOptions(): SelectOption[] {
        return [
            {
                value: LoanStatus.PrequalNotAccepted,
                label: 'Pre-Qualified, Not Accepted'
            } as SelectOption,
            {
                value: LoanStatus.PrequalAuctionLost,
                label: 'Pre-Qualified, Auction Lost'
            } as SelectOption,
            {
                value: LoanStatus.ConditionsMet,
                label: 'Conditions Met'
            } as SelectOption,
            {
                value: LoanStatus.PrequalDenied,
                label: 'Denied (Pre-Qualification)'
            } as SelectOption,
            {
                value: LoanStatus.BankDenied,
                label: 'Denied (Bank)'
            } as SelectOption,
            {
                value: LoanStatus.Withdrawn,
                label: 'Withdrawn'
            } as SelectOption
        ];
    }

    public GetActiveLoanStatuses(): number[] {
        return [LoanStatus.Submitted, LoanStatus.Processing, LoanStatus.NeedsInput, LoanStatus.Prequalified, LoanStatus.OfferSent, LoanStatus.PrequalOfferAccepted, LoanStatus.PrequalOfferRejected, LoanStatus.Approved, LoanStatus.DocumentsSent];
    }

    public GetClosedLoanStatuses(): number[] {
        return [LoanStatus.ConditionsMet, LoanStatus.BankDenied, LoanStatus.PrequalDenied, LoanStatus.Withdrawn, LoanStatus.PrequalNotAccepted, LoanStatus.PrequalAuctionLost];
    }

    public GetLoanActionOptions(loanStatus: number): SelectOption[] {
        // This will get expanded as we implement features down the road
        let options: SelectOption[] = [];

        if (this.GetActiveLoanStatuses().includes(loanStatus)) {
            options.push(...[
                {
                    value: LoanAction.RequestInput,
                    label: 'Request Input'
                } as SelectOption,
                {
                    value: LoanAction.Prequalify,
                    label: 'Mark Pre-Qualified'
                } as SelectOption,
                {
                    value: LoanAction.Approve,
                    label: 'Mark Approved (By Bank)'
                } as SelectOption,
                {
                    value: LoanAction.DocumentsSent,
                    label: 'Notify Documents Sent'
                } as SelectOption,
                {
                    value: LoanAction.ConditionsMet,
                    label: 'Conditions Met'
                } as SelectOption,
                {
                    value: LoanAction.Withdraw,
                    label: 'Withdraw'
                } as SelectOption,
                {
                    value: LoanAction.PrequalDeny,
                    label: 'Deny Loan (Pre-Qualification)'
                } as SelectOption,
                {
                    value: LoanAction.BankDeny,
                    label: 'Deny Loan (Bank Denial)'
                } as SelectOption,
            ])
        }

        // Saving below for future reference
        // For now we will just show all options
        // Checking first here to add as first option
        // if (this.GetActiveLoanStatuses().includes(loanStatus)) {
        //     options.push(
        //         {
        //             value: LoanAction.RequestInput,
        //             label: 'Request Input'
        //         } as SelectOption,
        //     );
        // }

        // if ([LoanStatus.Submitted, LoanStatus.Processing, LoanStatus.NeedsInput].includes(loanStatus)) {
        //     options.push(...[
        //         {
        //             value: LoanAction.Approve,
        //             label: 'Approve Loan'
        //         } as SelectOption,
        //     ]);
        // }

        // if (this.GetActiveLoanStatuses().includes(loanStatus)) {
        //     options.push(...[
        //         {
        //             value: LoanAction.BankDeny,
        //             label: 'Deny Loan'
        //         } as SelectOption,
        //         {
        //             value: LoanAction.ConditionsMet,
        //             label: 'Conditions Met'
        //         } as SelectOption,
        //     ])
        // }

        return options;
    }

    public GetApplicantName(applicant: Applicant | JointApplicant): string {
        return `${applicant.firstName} ${applicant.lastName}${applicant.suffix !== undefined ? ' ' + applicant.suffix : ''}`;
    }

    public JoinApplicantNames(applicant: Applicant, jointApplicant?: JointApplicant): string {
        if (jointApplicant != undefined && Object.keys(jointApplicant).length > 0) {
            return `${this.GetApplicantName(applicant)}, ${this.GetApplicantName(jointApplicant)}`;
        } else {
            return `${this.GetApplicantName(applicant)}`;
        }
    }

    public FormatPhoneNumber(phoneNumber: string): string {
        return `(${phoneNumber.toString().slice(0, 3)}) ${phoneNumber.toString().slice(3, 6)}-${phoneNumber.toString().slice(6, 10)}`;
    }

    public GetDateText(date: Date | string): string {
        return `${format(new Date(date), 'M/d/yy')}`;
    }

    public GetFullYearDateText(date: Date | string): string {
        return `${format(new Date(date), 'M/d/yyyy')}`;
    }

    public GetDateMonthNameFullYear(date: Date | string): string {
        return `${format(new Date(date), 'MMMM d, yyyy')}`
    }

    public BuildAmount(value?: number): string {
        return `${value?.toLocaleString() ?? '-'}`;
    }

    public BuildCurrencyAmount(value?: number): string {
        return `$${value?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? '-'}`;
    }

    public BuildDisplayId(id: number): string {
        var displayId = id.toString();
        var totalNumberOfZerosNeeded = 6 - displayId.length
        for (var i = 0; i < totalNumberOfZerosNeeded; i++) {
            displayId = '0' + displayId;
        }
        return '#' + displayId;
    }

    public GetAgriculturalProductMeasurers(measurement: AgriculturalProductMeasurers): string {
        switch (measurement) {
            case AgriculturalProductMeasurers.Acres:
                return 'Acre';
            case AgriculturalProductMeasurers.Bale:
                return 'Bale';
            case AgriculturalProductMeasurers.Bushels:
                return 'Bushel';
            case AgriculturalProductMeasurers.CwtPer100Pounds:
                return 'CwtPer100Pounds';
            case AgriculturalProductMeasurers.Flat:
                return 'Flat';
            case AgriculturalProductMeasurers.FourFiveBushel:
                return 'FourFiveBushel';
            case AgriculturalProductMeasurers.Gallon:
                return 'Gallon';
            case AgriculturalProductMeasurers.Hogshead:
                return 'Hogshead';
            case AgriculturalProductMeasurers.Lug:
                return 'Lug';
            case AgriculturalProductMeasurers.Other:
                return 'Other';
            case AgriculturalProductMeasurers.PerAcre:
                return 'Per Acre';
            case AgriculturalProductMeasurers.PerBoardFoot:
                return 'Per Board Foot';
            case AgriculturalProductMeasurers.PerHead:
                return 'Per Head';
            case AgriculturalProductMeasurers.Pound:
                return 'Pound';
            case AgriculturalProductMeasurers.Sacks:
                return 'Stack';
            case AgriculturalProductMeasurers.ThreeFourBushel:
                return 'ThreeFourBushel';
            case AgriculturalProductMeasurers.Tons:
                return 'Ton';
            default:
                return ''
        }
    }

    public BuildOtherTitle(naicsCode: NAICSCode): string {
        return `${naicsCode.title}${naicsCode.otherTitle?.length > 0 ? ` - ${naicsCode.otherTitle}` : ''}`;
    }

    public GetYearsExperience(farmingStartYear: number): string {
        let yearsExperience: number = getYear(new Date()) - farmingStartYear;
        if (yearsExperience === 0) yearsExperience = 1;
        return `${yearsExperience} year(s)`;
    }

    public GetCashFlowExpenseTypeText(expenseType: CashFlowExpenseType): string {
        switch (expenseType) {
            case CashFlowExpenseType.CarAndTruck:
                return 'Car and Truck';
            case CashFlowExpenseType.Chemicals:
                return 'Chemicals';
            case CashFlowExpenseType.Conservation:
                return 'Conservation';
            case CashFlowExpenseType.CustomHire:
                return 'Custom Hire';
            case CashFlowExpenseType.FeedSupplement:
                return 'Feed Supplement';
            case CashFlowExpenseType.FeedGrainAndRoughage:
                return 'Feed, Grain, and Roughage';
            case CashFlowExpenseType.FertilizersAndLime:
                return 'Fertilizers and Lime';
            case CashFlowExpenseType.FreightAndTrucking:
                return 'Freight and Trucking';
            case CashFlowExpenseType.GasFuelOil:
                return 'Gas/Fuel/Oil';
            case CashFlowExpenseType.Insurance:
                return 'Insurance';
            case CashFlowExpenseType.LaborHired:
                return 'Labor Hired';
            case CashFlowExpenseType.RentMachineryEquipmentVehicles:
                return 'Rent – Machinery/Equipment/Vehicles';
            case CashFlowExpenseType.RentLandAnimals:
                return 'Rent - Land/Animals';
            case CashFlowExpenseType.RepairsAndMaintenance:
                return 'Repairs and Maintenance';
            case CashFlowExpenseType.SeedsAndPlants:
                return 'Seeds and Plants';
            case CashFlowExpenseType.StorageAndWarehousing:
                return 'Storage and Warehousing';
            case CashFlowExpenseType.Supplies:
                return 'Supplies';
            case CashFlowExpenseType.TaxesRealEstate:
                return 'Taxes - Real Estate';
            case CashFlowExpenseType.Utilities:
                return 'Utilities';
            case CashFlowExpenseType.VeterinaryBreedingMedicine:
                return 'Veterinary/Breeding/Medicine';
            case CashFlowExpenseType.OtherExpenses:
                return 'Other Expenses';
            case CashFlowExpenseType.OtherIrrigation:
                return 'Other - Irrigation';
            case CashFlowExpenseType.OperatingLoanInterest:
                return 'Operating Loan Interest';
            default:
                return '';
        }
    }

    public GetCashFlowOtherFarmIncomeTypeText(otherFarmIncomeType: OtherFarmIncomeType): string {
        switch (otherFarmIncomeType) {
            case OtherFarmIncomeType.CustomHireIncome:
                return 'Custom Hire Income';
            case OtherFarmIncomeType.Other:
                return 'Other';
            default:
                return '';
        }
    }

    public GetCashFlowNonFarmIncomeTypeText(nonFarmIncomeType: NonFarmIncomeType): string {
        switch (nonFarmIncomeType) {
            case NonFarmIncomeType.PersonalIncome:
                return 'Personal Income';
            case NonFarmIncomeType.BusinessIncome:
                return 'Business Income';
            case NonFarmIncomeType.Other:
                return 'Other';
            default:
                return '';
        }
    }

    public GetCashFlowOtherExpenseTypeText(otherExpenseType: CashFlowOtherExpenseType): string {
        switch (otherExpenseType) {
            case CashFlowOtherExpenseType.Household:
                return 'Household Operating Expense';
            case CashFlowOtherExpenseType.CapitalPurchases:
                return 'Capital Purchase';
            default:
                return '';
        }
    }

    public GetPrimaryAgProductOptions(): SelectOption[] {
        // This will get expanded as we implement features down the road
        let options: SelectOption[] = [];

        options.push(...[
            {
                value: '111100',
                label: 'Grain'
            } as SelectOption,
            {
                value: '111110',
                label: 'Soybeans'
            } as SelectOption,
            {
                value: '111140',
                label: 'Wheat'
            } as SelectOption,
            {
                value: '111150',
                label: 'Corn'
            } as SelectOption,
            {
                value: '111160',
                label: 'Rice'
            } as SelectOption,
            {
                value: '111199',
                label: 'Cash Grains Not Elsewhere Classified'
            } as SelectOption,
            {
                value: '111211',
                label: 'Potatoes'
            } as SelectOption,
            {
                value: '111219',
                label: 'Vegetables and Melons'
            } as SelectOption,
            {
                value: '111330/111334',
                label: 'Berry Crops, Fruit, and Tree Nuts'
            } as SelectOption,
            {
                value: '111332',
                label: 'Grapes'
            } as SelectOption,
            {
                value: '111421/111422',
                label: 'Nursery/Greenhouse'
            } as SelectOption,
            {
                value: '111920',
                label: 'Cotton'
            } as SelectOption,
            {
                value: '111930/111991',
                label: 'Sugar Canes and Sugar Beets'
            } as SelectOption,
            {
                value: '111940',
                label: 'Hay/Forage'
            } as SelectOption,
            {
                value: '111998',
                label: 'Other Field Crops'
            } as SelectOption,
            {
                value: '112111',
                label: 'Beef Cattle Except Feedlots'
            } as SelectOption,
            {
                value: '112112',
                label: 'Beef Cattle Feedlots'
            } as SelectOption,
            {
                value: '112120',
                label: 'Dairy Farms'
            } as SelectOption,
            {
                value: '112210',
                label: 'Hogs'
            } as SelectOption,
            {
                value: '112310/112320',
                label: 'Poultry and Eggs'
            } as SelectOption,
            {
                value: '112330',
                label: 'Turkeys'
            } as SelectOption,
            {
                value: '112410',
                label: 'Sheep'
            } as SelectOption,
            {
                value: '112420',
                label: 'Goats'
            } as SelectOption,
            {
                value: '112511/112512',
                label: 'Aquaculture/Fish'
            } as SelectOption,
            {
                value: '112990',
                label: 'General Farms Primarily Livestock'
            } as SelectOption,
            {
                value: '113000',
                label: 'Cattle Breeding and Forestry'
            } as SelectOption,
            {
                value: '113310',
                label: 'Logging'
            } as SelectOption,
            {
                value: '115113',
                label: 'Crop Harvesting Primarily by Machine'
            } as SelectOption,
            {
                value: '115210',
                label: 'Livestock Services'
            } as SelectOption,
            {
                value: '484110',
                label: 'Local Trucking W/O Storage'
            } as SelectOption,
            {
                value: '999999',
                label: 'Non-Classifiable Establishment'
            } as SelectOption,
        ])

        return options;
    }
}

const loanApplicationUtility = new LoanApplicationUtility();
export default loanApplicationUtility;